'use client';
import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { track } from '@repo/ui/src/services/analytics/utils';
import { recipeListFiltered, recipeListViewed } from '@repo/ui/src/services/analytics';
import PaginatedGrid, { GridType } from '@components/PaginatedGrid';
import GridItem from './_GridItem';
import GridHeader from './_GridHeader';
import { mapFacetsToFilters } from './_facetFilters';
import type { Data } from '@typings/productGrid';
import type { RecipeListFiltered } from '@repo/ui/src/services/analytics/types';
import type { CategoryFilters } from '@api/getRecipeContent';

interface Props {
  recipeFilters: CategoryFilters;
  _data?: Data;
}

interface FilterSelection {
  filterType: string;
  filterName: string;
}

type GridFilters = Record<string, Array<string>>;

const RecipeCategoryGrid = ({ recipeFilters, _data }: Props) => {
  const { query, path, locale } = useCurrentUrl();
  const { t } = useTranslation();

  const facetTitles: Record<string, string> = {
    mealType: t('recipeCategory.facets.mealType', undefined, { default: 'Meal Type' }),
    complexity: t('recipeCategory.facets.complexity', undefined, { default: 'Difficulty' }),
    hardwood: t('recipeCategory.facets.hardwood', undefined, { default: 'Hardwood' }),
    holiday: t('recipeCategory.facets.holiday', undefined, { default: 'Holiday' }),
    cookingMethod: t('recipeCategory.facets.cookingMethod', undefined, {
      default: 'Cooking Method',
    }),
    diet: t('recipeCategory.facets.diet', undefined, { default: 'Diet' }),
  };

  const urlFilters: GridFilters = {};
  if (query) {
    const queryParams = query as unknown as Record<string, string>;
    const recipeFilterNames = recipeFilters.map((filter) => filter.name);
    Object.keys(queryParams).forEach((key) => {
      if (recipeFilterNames.includes(key)) {
        if (!urlFilters[key]) {
          urlFilters[key] = [queryParams[key]];
        } else {
          urlFilters[key].push(queryParams[key]);
        }
      }
    });
  }

  const [gridData, setGridData] = useState<Data>(
    _data ??
      ({
        page: 0,
        nbPages: 0,
        nbHits: 0,
        results: [],
      } as unknown as Data),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [gridFilters, setGridFilters] = useState<GridFilters>(urlFilters);
  const isFirstMount = useRef(true);

  const setFilters = async (filter: FilterSelection) => {
    const { filterType, filterName } = filter;
    setGridFilters((filters) => {
      const _filters = { ...filters };
      if (_filters?.[filterType]?.includes(filterName)) {
        _filters[filterType] = _filters[filterType]?.filter((f) => f !== filterName);
      } else {
        if (Array.isArray(_filters[filterType])) {
          _filters[filterType] = [..._filters[filterType], filterName];
        } else {
          _filters[filterType] = [filterName];
        }
      }
      return _filters;
    });
  };

  const clearFilters = () => {
    setGridFilters({});
  };

  const logListFiltered = useCallback(
    (hits: Array<any>, overrideIndex: number | null = null) => {
      track(recipeListFiltered, {
        list_id: path.replace('/recipes', ''),
        filters: gridFilters as unknown as RecipeListFiltered['filters'],
        recipes: hits.map((recipe: any, index: number) => ({
          recipe_id: recipe?.id,
          name: recipe?.recipeMetaSection?.title,
          position: overrideIndex ? overrideIndex + index : index + 1,
          url: `${window.location.protocol}://${window.location.hostname}/${recipe?._meta?.deliveryKey}`,
          image_url: recipe?.assets?.[0]?.url,
        })),
      });
    },
    [path, gridFilters],
  );

  const logListViewed = useCallback(
    (hits: Array<any>, overrideIndex: number | null = null) => {
      track(recipeListViewed, {
        list_id: path.replace('/recipes', ''),
        recipes: hits.map((recipe: any, index: number) => ({
          recipe_id: recipe?.id,
          name: recipe?.recipeMetaSection?.title,
          position: overrideIndex ? overrideIndex + index + 1 : index + 1,
          url: `${window.location.protocol}://${window.location.hostname}/${recipe?._meta?.deliveryKey}`,
          image_url: recipe?.assets?.[0]?.url,
        })),
      });
    },
    [path],
  );

  const loadData = useCallback(
    async (
      { page }: { page: number },
      controller: AbortController,
      startIndex = 0,
    ) => {
      const res = await axios.post(
        '/api/recipe-category/getRecipeCategory',
        {
          categories: recipeFilters,
          page,
          filters: gridFilters,
          lang: locale,
        },
        { signal: controller.signal },
      );
      
      const hits = res?.data?.hits ?? [];
      if (!isEmpty(gridFilters)) {
        logListFiltered(hits, startIndex);
      } else {
        logListViewed(hits, startIndex);
      }

      return res;
    },
    [gridFilters, recipeFilters, locale, logListViewed, logListFiltered],
  );

  useEffect(() => {
    const controller = new AbortController();

    const reload = async () => {
      setIsLoading(true);
      try {
        const res = await loadData({ page: 0 }, controller);
        setGridData(res?.data ?? {});
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error('Failed to load recipe category data', e);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      reload();
    }
    return () => controller.abort();
  }, [gridFilters, loadData]);

  return (
    <>
      <GridHeader
        filters={mapFacetsToFilters(gridData.facets, facetTitles)}
        currentFilters={gridFilters}
        setFilters={setFilters}
        nbHits={gridData.nbHits}
        clearFilters={clearFilters}
      />
      <PaginatedGrid
        isLoading={isLoading}
        _data={gridData}
        loadData={loadData}
        GridItemComponent={GridItem}
        type={GridType.Recipe}
      />
    </>
  );
};

export default RecipeCategoryGrid;
